/** @module components/hot-spot-quiz */


import Model from '@lernetz/ts-lib/src/core/model/Model';
import View from '@lernetz/ts-lib/src/core/view/View';

import {Point} from './Point';

import {CanShowCorrectness} from '../quiz/CanShowCorrectness';

import {Spot as template} from '../../templates/hot-spot-quiz';


/**
 * Specifies the initial state of a [[Spot]].
 */
export interface SpotInitialState {

    /**
     * Sets the X coordinate, normalized to the range [0..1].
     */
    x: number;

    /**
     * Sets the Y coordinate, normalized to the range [0..1].
     */
    y: number;
}


/**
 * A placed spot in a [[HotSpotQuiz]].
 */
export class Spot extends Model implements Point, CanShowCorrectness {

    /**
     * Initializes a new instance of the [[Spot]] class.
     *
     * @param initialState The initial state for the new instance.
     * @param data An optional object to be passed through to the base class' ([[Model]]) constructor.
     */
    public constructor(initialState: SpotInitialState, data: object = {}) {
        super({
            x: 0.5,
            y: 0.5,
            containedInHotSpotArea: false,
            showCorrectness: false,
            ...initialState,
            ...data
        })
    }

    /** @inheritdoc */
    public readonly x: number;

    /** @inheritdoc */
    public readonly y: number;

    /**
     * Gets or sets whether this point is contained in a hot-spot area.
     */
    public containedInHotSpotArea: boolean;

    /**
     * Gets whether correctness should be displayed for this spot.
     */
    public showCorrectness: boolean;
}


/**
 * Base `View` class for rendering a [[Spot]].
 *
 * This class takes care of setting the correct CSS for the spot's normalized coordinates.
 */
export class SpotView extends View<Spot> {

    public constructor() {
        super({ template });
    }

    init(): void {
        const node = <HTMLElement> this.node;
        node.style.top = `${this.data.y * 100}%`;
        node.style.left = `${this.data.x * 100}%`;
    }
}
